/**
 * Mot de passe temporaire : généré dans le navigateur, montré une seule fois à l'administrateur pour qu'il le
 * transmette, puis remplacé par l'employé à sa première connexion. Il n'est jamais conservé par RECA.
 */

const lower = 'abcdefghijkmnpqrstuvwxyz'
const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
const digits = '23456789'
// Symboles faciles à dicter et à taper sur une tablette.
const symbols = '!#$%*+-=?@'
const alphabet = lower + upper + digits + symbols

export const temporaryPasswordLength = 20

function randomIndex(max: number, random: (size: number) => Uint32Array): number {
  // Rejet des valeurs du dernier intervalle incomplet : aucun caractère n'est favorisé.
  const limit = Math.floor(0x1_0000_0000 / max) * max
  for (;;) {
    const value = random(1)[0] ?? 0
    if (value < limit) return value % max
  }
}

const cryptoRandom = (size: number) => crypto.getRandomValues(new Uint32Array(size))

export function generateTemporaryPassword(random: (size: number) => Uint32Array = cryptoRandom): string {
  const pick = (set: string) => set.charAt(randomIndex(set.length, random))
  const characters = [pick(lower), pick(upper), pick(digits), pick(symbols)]
  while (characters.length < temporaryPasswordLength) characters.push(pick(alphabet))
  // Mélange de Fisher-Yates : les quatre classes garanties ne restent pas en tête.
  for (let index = characters.length - 1; index > 0; index -= 1) {
    const swap = randomIndex(index + 1, random)
    const current = characters[index] ?? ''
    characters[index] = characters[swap] ?? ''
    characters[swap] = current
  }
  return characters.join('')
}

export type PasswordIssue = 'LENGTH' | 'TOO_LONG' | 'CLASSES'

/** Même règle que la fonction Edge `employee-accounts` : 12 à 72 caractères, trois classes sur quatre. */
export function passwordIssues(password: string): PasswordIssue[] {
  const issues: PasswordIssue[] = []
  if (password.length < 12) issues.push('LENGTH')
  if (password.length > 72) issues.push('TOO_LONG')
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((pattern) =>
    pattern.test(password),
  ).length
  if (classes < 3) issues.push('CLASSES')
  return issues
}

export const passwordIssueLabels: Record<PasswordIssue, string> = {
  LENGTH: 'Au moins 12 caractères.',
  TOO_LONG: 'Au plus 72 caractères.',
  CLASSES: 'Au moins trois types parmi : minuscules, majuscules, chiffres, symboles.',
}
